import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import { MdClose } from "react-icons/md";
import { IconButton } from "components/button";

// Chakra runs headless (no ChakraProvider/theme), so layout that the theme
// would normally supply — centering, full-viewport container, stacking — is
// set explicitly here.
const OVERLAY_Z_INDEX = 70;

const SIZES = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
};

const CONTAINER_PROPS = {
  zIndex: OVERLAY_Z_INDEX,
  top: 0,
  bottom: 0,
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  padding: "1rem",
};

/**
 * Dialog — centered modal with focus trap, Esc/overlay close and focus return.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose Called on Esc, overlay click and the close button.
 * @param {React.ReactNode} [props.title] Rendered as the dialog heading (labels the dialog).
 * @param {React.ReactNode} [props.description] Muted text under the title.
 * @param {React.ReactNode} [props.footer] Actions, right-aligned with gap-2.
 * @param {"sm"|"md"|"lg"|"xl"} [props.size="md"] max-w-sm / max-w-lg / max-w-2xl / max-w-4xl.
 * @param {React.RefObject<HTMLElement>} [props.initialFocusRef] Element focused on open (defaults to the first focusable).
 * @param {React.ReactNode} props.children Scrollable body content.
 */
function Dialog({
  isOpen,
  onClose,
  title,
  description,
  footer,
  size = "md",
  initialFocusRef,
  children,
}) {
  const hasHeader = Boolean(title || description);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      trapFocus
      returnFocusOnClose
      closeOnEsc
      closeOnOverlayClick
      initialFocusRef={initialFocusRef}
    >
      <ModalOverlay
        className="bg-overlay/60 backdrop-blur-[2px]"
        zIndex={OVERLAY_Z_INDEX}
      />
      <ModalContent
        containerProps={CONTAINER_PROPS}
        className={`relative flex max-h-[85vh] w-full flex-col overflow-hidden rounded-card bg-surface text-fg shadow-overlay ${
          SIZES[size] || SIZES.md
        }`}
      >
        {hasHeader && (
          <div className="shrink-0 px-6 pr-14 pt-6">
            {title && (
              <ModalHeader as="h2" className="text-heading">
                {title}
              </ModalHeader>
            )}
            {description && (
              <p className="mt-1 text-sm text-fg-muted">{description}</p>
            )}
          </div>
        )}
        <ModalBody
          className={`min-h-0 flex-1 overflow-y-auto px-6 ${
            hasHeader ? "pt-4" : "pt-6"
          } ${footer ? "pb-4" : "pb-6"}`}
        >
          {children}
        </ModalBody>
        {footer && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 px-6 pb-6 pt-2">
            {footer}
          </div>
        )}
        {/* Last in DOM order so initial focus lands on the first body/footer control. */}
        <IconButton
          icon={<MdClose />}
          label="Close"
          size="sm"
          onClick={onClose}
          className="absolute right-4 top-4"
        />
      </ModalContent>
    </Modal>
  );
}

export default Dialog;
